/**
 * Market data utilities.
 *
 * Single source of truth for tick rounding and market calculations.
 */

/**
 * Round a price to the nearest tick.
 * @param {number} price - The price to round
 * @param {number} tickSize - Tick size (default: 0.25 for ES)
 * @returns {number} Rounded price
 */
export function roundToTick(price, tickSize = 0.25) {
  return Math.round(price / tickSize) * tickSize;
}

/**
 * Current hour + weekday in US/Eastern (the trading session timezone).
 * @returns {{hour: number, weekday: number}} weekday: Sun=0 .. Sat=6
 */
export function getETTime() {
  const now = new Date();
  const etFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    hour: 'numeric',
    hour12: false,
    weekday: 'short',
  });
  const parts = etFormatter.formatToParts(now);
  const hour = parseInt(parts.find(p => p.type === 'hour').value, 10);
  const weekday = parts.find(p => p.type === 'weekday').value;
  const weekdayMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  return { hour, weekday: weekdayMap[weekday] };
}

/**
 * Snap a YYYY-MM-DD date to the nearest prior weekday (trading day):
 * Sunday -> Friday (-2), Saturday -> Friday (-1), weekdays unchanged.
 * Returns YYYY-MM-DD, or null for empty input.
 */
export function snapToWeekday(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr + 'T12:00:00');
  const dow = d.getDay();
  if (dow === 0) d.setDate(d.getDate() - 2);
  else if (dow === 6) d.setDate(d.getDate() - 1);
  return d.toISOString().split('T')[0];
}

/**
 * Move one weekday from a YYYY-MM-DD string, skipping weekends. Noon-anchored so
 * the UTC date never rolls across midnight. dir: -1 earlier, +1 later.
 */
export function stepWeekday(dateStr, dir) {
  const d = new Date(dateStr + 'T12:00:00');
  do { d.setDate(d.getDate() + dir); } while (d.getDay() === 0 || d.getDay() === 6);
  return d.toISOString().split('T')[0];
}

/**
 * Current calendar date in US/Eastern (YYYY-MM-DD), DST-safe. The en-CA
 * locale formats as ISO. Must NOT come from toISOString(): the UTC date
 * rolls ahead of the ET date at 20:00 ET (EDT) / 19:00 ET (EST), which
 * made every evening session-date computed from it one day too far.
 */
function getETDateStr() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' })
    .format(new Date());
}

/**
 * Get the latest session date (YYYY-MM-DD), including the next day after
 * settlement: after 6 PM ET (Globex open) the next session has begun.
 * All arithmetic is on the ET calendar date, noon-anchored so setDate
 * never rolls across midnight (same pattern as stepWeekday).
 */
export function getLatestSessionDate() {
  const { hour, weekday } = getETTime();
  const d = new Date(getETDateStr() + 'T12:00:00');

  if (weekday === 6) {
    d.setDate(d.getDate() - 1);                        // Saturday -> Friday's session
  } else if (weekday === 0) {
    if (hour >= 18) d.setDate(d.getDate() + 1);        // Sun 6 PM+ -> Monday
    else d.setDate(d.getDate() - 2);                   // Sun day -> Friday
  } else if (hour >= 18) {
    d.setDate(d.getDate() + (weekday === 5 ? 3 : 1));  // Fri->Mon, else tomorrow
  }
  return d.toISOString().split('T')[0];
}

/**
 * Globex session date (YYYY-MM-DD) a timestamp belongs to: the ET calendar
 * date, advanced one day when the ET hour is >= 18 (the 18:00 ET Globex
 * open rolls trading into the next session). Timestamp-parameterized
 * sibling of getLatestSessionDate's rollover rule.
 * @param {number} tsSec - Unix timestamp in seconds
 */
export function sessionDateOf(tsSec) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', hourCycle: 'h23',
  }).formatToParts(new Date(tsSec * 1000));
  const get = (type) => parts.find((p) => p.type === type)?.value;
  const dateStr = `${get('year')}-${get('month')}-${get('day')}`;
  if (parseInt(get('hour'), 10) >= 18) {
    const d = new Date(dateStr + 'T12:00:00');
    d.setDate(d.getDate() + 1);
    return d.toISOString().split('T')[0];
  }
  return dateStr;
}

/**
 * ISO date (YYYY-MM-DD) of the currently OPEN ES session, or null when no
 * session is live (17:00-18:00 ET settlement break, weekend closure).
 */
export function getOpenSessionDate() {
  const { hour, weekday } = getETTime();
  if (hour === 17) return null;                 // settlement break
  if (weekday === 6) return null;               // Saturday
  if (weekday === 0 && hour < 18) return null;  // Sunday pre-reopen
  if (weekday === 5 && hour >= 17) return null; // Friday after close
  return getLatestSessionDate();
}
